<template>
  <div class="language-selector">
    <div class="locale-pill">
      <button
        v-for="locale in locales"
        :key="locale.code"
        class="pill-option"
        :class="{ active: locale.code === currentLanguage }"
        type="button"
        :title="locale.name"
        @click="switchLanguage(locale.code)"
      >
        <span class="flag">{{ locale.flag }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { ElMessage } from "element-plus";
import { setLocale, getCurrentLocale, availableLocales } from "@/locales";
import type { LocaleCode } from "@/locales";

const currentLanguage = computed<LocaleCode>(() => getCurrentLocale() as LocaleCode);
const locales = computed(() => availableLocales);

const successMessages: Record<LocaleCode, string> = {
  en: "Language switched to English",
  de: "Sprache wurde auf Deutsch umgestellt",
  zh: "语言已切换为中文",
};

const switchLanguage = (code: LocaleCode) => {
  if (code === currentLanguage.value) {
    return;
  }

  setLocale(code);
  ElMessage.success({
    message: successMessages[code],
    duration: 1000, // 1秒后自动关闭
  });
};
</script>

<style scoped>
.language-selector {
  display: flex;
  align-items: center;
}

.locale-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px;
  background: rgba(102, 126, 234, 0.08);
  border-radius: 12px;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.12);
  border: 1.5px solid rgba(102, 126, 234, 0.15);
  gap: 3px;
  transition: all 0.3s ease;
}

.locale-pill:hover {
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
  border-color: rgba(102, 126, 234, 0.25);
}

.pill-option {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.pill-option::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.pill-option:hover::before {
  opacity: 1;
}

.pill-option:hover {
  transform: translateY(-2px) scale(1.08);
}

.pill-option.active {
  background: linear-gradient(135deg, #667eea, #764ba2);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.35),
              inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.pill-option.active::before {
  display: none;
}

.pill-option.active:hover {
  transform: translateY(-2px) scale(1.1);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.45),
              inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.flag {
  font-size: 20px;
  line-height: 1;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: all 0.3s ease;
}

.pill-option:hover .flag {
  filter: drop-shadow(0 4px 8px rgba(102, 126, 234, 0.3));
  transform: scale(1.1);
}

.pill-option.active .flag {
  filter: drop-shadow(0 2px 6px rgba(255, 255, 255, 0.5));
}

@media (max-width: 768px) {
  .locale-pill {
    padding: 3px;
    gap: 2px;
  }

  .pill-option {
    width: 32px;
    height: 32px;
  }

  .flag {
    font-size: 18px;
  }
}
</style>
